// Trade Group TypeScript Types

export enum TradeGroupType {
  REGULAR_LADDER = 'REGULAR_LADDER',
  FUTURE_LADDER = 'FUTURE_LADDER',
  MOMENTUM_SWING = 'MOMENTUM_SWING',
  VOLATILITY_LADDER = 'VOLATILITY_LADDER',
  BOLLINGER_REVERSION = 'BOLLINGER_REVERSION',
  RSI_EXTREMES = 'RSI_EXTREMES',
  MACD_REVERSION = 'MACD_REVERSION',
  MACD_MOMENTUM = 'MACD_MOMENTUM',
  VWAP_REVERSION = 'VWAP_REVERSION',
  ICHIMOKU_CLOUD = 'ICHIMOKU_CLOUD',
  PIVOT_POINT_TRADING = 'PIVOT_POINT_TRADING',
  MA_CROSSOVER = 'MA_CROSSOVER',
  SUPPORT_RETEST_DIP_LADDER = 'SUPPORT_RETEST_DIP_LADDER',
  ORCHESTRATED_LADDER = 'ORCHESTRATED_LADDER',
  UNKNOWN = 'UNKNOWN'
}

export enum QuoteDistributionType {
  UNIFORM = 'UNIFORM',
  VERY_SLIGHT = 'VERY_SLIGHT',
  SLIGHT = 'SLIGHT',
  SHALLOW = 'SHALLOW',
  BALANCED = 'BALANCED',
  LINEAR = 'LINEAR',
  AGGRESSIVE = 'AGGRESSIVE',
  STEEP = 'STEEP',
  EXTREME = 'EXTREME'
}

export enum StrategyMetricPeriod {
  ULTRA_SHORT = 'ULTRA_SHORT', // 1-minute candles
  VERY_SHORT = 'VERY_SHORT', // 5-minute candles
  SHORT = 'SHORT', // 15-minute candles
  MEDIUM_SHORT = 'MEDIUM_SHORT', // 30-minute candles
  MEDIUM = 'MEDIUM', // 1-hour candles (default)
  LONG = 'LONG', // 4-hour candles (RSI/MACD only)
  VERY_LONG = 'VERY_LONG' // 1-day candles (RSI/MACD only)
}

export interface TradePreview {
  clientOrderId: string;
  productId: string;
  side: 'BUY' | 'SELL';
  baseSize: string;
  limitPrice: string;
  totalQuote: string;
  totalFee: string;
  sellTargetPrice: string;
  targetPercent: string;
  targetProfit: string;
  trailingStopEnabled: boolean;
  trailingStopPercent: string | null;
}

export interface TradePreviewResponse {
  tradeGroupId: string;
  totalTrades: number;
  totalInvestment: string;
  totalFees: string;
  totalExpectedProfit: string;
  previews: TradePreview[];
}

/**
 * Request type matching backend LadderedTradeRequest.java exactly
 */
export interface LadderedTradeRequest {
  productId: string;
  totalQuoteSize: string;
  minPrice: string;
  maxPrice: string;
  numLadders: number;
  preview: boolean;
  tradeGroupId?: string;
  name: string;
  keepAlive: boolean;
  tradesPerLadder: number;
  targetPercentsPerLadder: string[];
  quotePercentsPerLadder?: string[] | null;
  quoteDistributionType: QuoteDistributionType;
  targetPercentMultiplier: string;
  tradeGroupType: TradeGroupType;
  trailingPercentTrigger?: string | null;
  trailingStopEnabled: boolean;
  trailingStopPercent?: string | null;
  trailingUpdateThreshold?: string | null;
  trailingStopLimitBufferPct?: string | null;
  profitReinvestmentPct?: string | null;
}

export interface WizardFormData {
  // Step 1: Strategy Selection
  strategyType: TradeGroupType;

  // Step 2: Basic Configuration
  productId: string;
  tradeGroupName: string;
  totalInvestment: string;
  minPrice: string;
  maxPrice: string;
  numLadders: number;
  tradesPerLadder: number;

  // Step 3: Advanced Settings
  profitTargets: string[]; // Array of profit targets per trade in ladder
  quotePercents: string[]; // Array of quote percentages per trade in ladder
  quoteDistributionType: QuoteDistributionType;
  targetPercentMultiplier: string;
  keepAlive: boolean;
  paperTradeMode?: boolean; // Paper trade mode flag (optional, defaults to true for new strategies)
  trailingStopsEnabled: boolean;
  trailingStopPercent: string;
  trailingPercentTrigger: string; // For Future Ladder activation
  profitReinvestmentPct: string;

  // Momentum Swing specific fields
  momentumQuoteSize: string; // Total position size for momentum swing
  momentumStrength: string; // Optional momentum indicator (0-1)
  expectedEntryPrice: string; // Optional reference price

  // Volatility Ladder specific fields
  volatilityPeriod: VolatilityPeriod;
  numberOfRungs: number; // Number of buy orders
  ladderSpacingPct: string; // Spacing between rungs (e.g., "2.0" = 2%)
  profitTargetPct: string; // Profit target (e.g., "2.0" = 2%)
  initialDeploymentPct: string;
  lowVolatilityThreshold: string;
  highVolatilityThreshold: string;
  extremeVolatilityThreshold: string;
  lowVolatilityPositionMultiplier: string;
  highVolatilityPositionMultiplier: string;
  rebalanceSensitivity: string;
  minRebalanceIntervalHours: string;
  volatilityCheckIntervalHours: string;

  // Adaptive mode fields (AVL-US-009)
  adaptiveMode: boolean;
  lowerBoundPrice: string;
  upperBoundPrice: string;

  // Bollinger Reversion specific fields
  strategyMetricPeriod?: StrategyMetricPeriod; // Trading timeframe for candle granularity
}

/**
 * Request type for Momentum Swing trade group creation
 * Matches backend MomentumSwingRequest.java
 */
export interface MomentumSwingRequest {
  productId: string;
  quoteSize: string;
  name: string;
  origin?: string;
  preview?: boolean;
  momentumStrength?: string;
  expectedEntryPrice?: string;
}

/**
 * Volatility analysis period enum
 * Matches backend VolatilityPeriod.java
 */
export enum VolatilityPeriod {
  TEST = 'TEST',         // 1 hour
  ULTRA_SHORT = 'ULTRA_SHORT', // 3 hours
  VERY_SHORT = 'VERY_SHORT', // 6 hours
  SHORT = 'SHORT',           // 24 hours
  MEDIUM = 'MEDIUM',         // 7 days
  MEDIUM_LONG = 'MEDIUM_LONG', // 14 days
  LONG = 'LONG',             // 21 days
  VERY_LONG = 'VERY_LONG'    // 30 days
}

/**
 * Volatility regime enum
 * Represents current market volatility state
 */
export enum VolatilityRegime {
  LOW = 'LOW',
  NORMAL = 'NORMAL',
  HIGH = 'HIGH',
  EXTREME = 'EXTREME'
}

/**
 * Volatility Ladder specific attributes
 * Matches backend VolatilityLadderTradeGroup.java
 */
export interface VolatilityLadderDetails {
  // Configuration fields (static)
  targetOrderAmount: number | null;
  numberOfRungs: number | null;
  ladderSpacingPct: number | null;  // e.g., 0.0032 = 0.32%
  ladderSpacingAmount: number | null;  // Dollar amount between rungs
  volatilityPeriod: string | null;  // VERY_SHORT, SHORT, MEDIUM, etc.
  initialDeploymentPct: number | null;  // 0.15 = 15%
  profitTargetPct: number | null;  // 0.02 = 2%
  adaptiveMode: boolean;
  lowerBoundPrice: number | null;
  upperBoundPrice: number | null;
  
  // Threshold configuration
  lowVolatilityThreshold: number | null;
  highVolatilityThreshold: number | null;
  extremeVolatilityThreshold: number | null;
  lowVolatilityPositionMultiplier: number | null;
  highVolatilityPositionMultiplier: number | null;
  rebalanceSensitivity: number | null;
  minRebalanceIntervalHours: number | null;
  
  // Current state (dynamic)
  currentAtr: number | null;
  averageAtr: number | null;
  volatilityPercentile: number | null;  // 0-1.0
  previousVolatilityPercentile: number | null;
  currentVolatilityRegime: string | null;  // LOW, NORMAL, HIGH, EXTREME
  lastVolatilityCheck: number | null;  // Timestamp
  volatilityCheckIntervalHours: number | null;
  lastRebalanceTime: number | null;  // Timestamp
  capitalDeployedPct: number | null;  // 0-1.0
  lastCapitalDeployment: number | null;  // Timestamp
  lastDipBuyingTime: number | null;  // Timestamp
  candleCount: number | null;
}

/**
 * Request type for Volatility Ladder trade group creation
 * Matches backend VolatilityLadderTradeRequest.java
 */
export interface VolatilityLadderTradeRequest {
  productId: string;
  name: string;
  totalQuoteSize: string;
  numberOfRungs: number; // Number of buy orders
  ladderSpacingPct: string; // Spacing between rungs (decimal: 0.02 = 2%)
  profitTargetPct: string; // Profit target (decimal: 0.02 = 2%)
  minPrice: string;
  maxPrice: string;
  numLadders: number; // Legacy - kept for backward compatibility
  tradesPerLadder: number; // Legacy - kept for backward compatibility
  targetPercentsPerLadder: string[];
  quotePercentsPerLadder?: string[] | null;
  quoteDistributionType: QuoteDistributionType;
  targetPercentMultiplier: string;
  volatilityPeriod: VolatilityPeriod;
  initialDeploymentPct?: string;
  lowVolatilityThreshold?: string;
  highVolatilityThreshold?: string;
  extremeVolatilityThreshold?: string;
  lowVolatilityPositionMultiplier?: string;
  highVolatilityPositionMultiplier?: string;
  rebalanceSensitivity?: string;
  minRebalanceIntervalHours?: number;
  volatilityCheckIntervalHours?: number;
  // Adaptive mode fields (AVL-US-009)
  adaptiveMode?: boolean;
  lowerBoundPrice?: string;
  upperBoundPrice?: string;
  keepAlive: boolean;
  trailingStopEnabled: boolean;
  trailingStopPercent?: string | null;
  profitReinvestmentPct?: string | null;
  preview?: boolean;
  origin?: string;
}

/**
 * Request type for Bollinger Reversion trade group creation
 * Matches backend - requires productId and quoteSize, other parameters from global config
 */
export interface BollingerReversionRequest {
  productId: string;
  name: string;
  quoteSize: string;  // Initial allocation in quote currency (e.g., USDC)
  paperTradeMode?: boolean;  // Optional paper trade mode flag (defaults to true if not specified)
  strategyMetricPeriod?: StrategyMetricPeriod;  // Trading timeframe/candle granularity (defaults to MEDIUM if not specified)
}

export interface RsiExtremesRequest {
  productId: string;
  name: string;
  quoteSize: string;  // Initial allocation in quote currency (e.g., USDC)
  paperTradeMode?: boolean;  // Optional paper trade mode flag (defaults to true if not specified)
  strategyMetricPeriod?: StrategyMetricPeriod;  // Trading timeframe/candle granularity (defaults to MEDIUM if not specified)
}

/**
 * Request type for MACD Reversion trade group creation
 * Matches backend - requires productId and quoteSize, other parameters from global config
 */
export interface MacdReversionRequest {
  productId: string;
  name: string;
  quoteSize: string;  // Initial allocation in quote currency (e.g., USDC)
  paperTradeMode?: boolean;  // Optional paper trade mode flag (defaults to true if not specified)
  strategyMetricPeriod?: StrategyMetricPeriod;  // Trading timeframe/candle granularity (defaults to MEDIUM if not specified)
}

/**
 * Request type for MACD Momentum trade group creation (REQ-015, REQ-016).
 *
 * Crossover-momentum entry strategy (distinct from MACD_REVERSION). The UI
 * camelCase shape; `createMacdMomentumTradeGroup` maps each field to its
 * snake_case wire counterpart before POST. The create response is consumed
 * VERBATIM (no {data, metadata} envelope unwrap — AD-008). Sibling: MacdReversionRequest.
 *
 * Operator-tunable knobs surfaced in the wizard (profit target %, stop-loss %,
 * trailing toggle + activation %) flow through; all other parameters resolve
 * from the `macd.momentum.` global config defaults.
 */
export interface MacdMomentumRequest {
  productId: string;
  name: string;
  quoteSize: string;  // Initial allocation in quote currency (e.g., USDC)
  paperTradeMode?: boolean;  // Optional paper trade mode flag (defaults to true if not specified)
  strategyMetricPeriod?: StrategyMetricPeriod;  // Trading timeframe/candle granularity (defaults to MEDIUM if not specified)
  // Profit target / stop-loss are config-tier tunables (read at runtime from MACDMomentumConfigProvider),
  // NOT per-group overrides — so they are deliberately not collected here. Change them in config.global_config.
  trailingStopEnabled: boolean;  // Trailing-stop opt-in (default ON)
  // Trailing-stop group-tier overrides (omit ⇒ inherit product/global via TrailingStopResolver). Percent
  // strings (e.g., "3" = 3%); only meaningful when trailing is enabled.
  trailingStopPercent?: string;          // Trailing distance below the peak
  trailingUpdateThreshold?: string;      // Min advance past the peak before re-ratcheting
  trailingStopLimitBufferPct?: string;   // Stop-limit buffer below the trailing trigger
  trailingStopActivationPct?: string;    // Gain that activates trailing
}

/**
 * Request type for VWAP Reversion trade group creation.
 * REQ-014: VWAP-specific config knobs (vwapPeriod, bandSigma) are required and persist
 * on the trade group, not the cycle. REQ-015 TGS-readiness: quoteSize flows verbatim
 * into original_quote_size; the bot uses it without runtime adjustment.
 */
export interface VwapReversionRequest {
  productId: string;
  name: string;
  quoteSize: string;  // Initial allocation in quote currency (e.g., USDC)
  paperTradeMode?: boolean;  // Optional paper trade mode flag (defaults to true if not specified)
  strategyMetricPeriod: StrategyMetricPeriod;  // Trading timeframe/candle granularity
  vwapPeriod: number;  // Rolling VWAP window size in candles (e.g., 20)
  bandSigma: string;   // σ-band multiplier as decimal string (e.g., "2.0"); BigDecimal-safe
  // Group-tier ATR-stop overrides (all optional). undefined = inherit (resolve via product/global).
  atrStopEnabled?: boolean;     // true = ATR-scaled stop, false = flat % stop; omit to inherit
  atrStopMultiplier?: string;   // ATR multiplier as decimal string; omit to inherit the default
  atrMaxStopPct?: string;       // ceiling: max stop width as a fraction of entry (e.g. "0.05" = 5%); omit to inherit
}

/**
 * Stop-style options for the Ichimoku Cloud strategy.
 * Matches backend `trend_following_trade_group.stop_style` (REQ-017).
 */
export type IchimokuStopStyle = 'KIJUN_SEN' | 'SENKOU_SPAN_B' | 'CLOUD_FAR_EDGE';

/**
 * Request type for Ichimoku Cloud trade group creation (REQ-022, REQ-023, REQ-026).
 *
 * The UI camelCase shape; `createIchimokuCloudTradeGroup` manually maps
 * each field to its snake_case wire counterpart before POST.
 * Sibling: VwapReversionRequest.
 */
export type IchimokuExitMode = 'ICHIMOKU_SIGNALS' | 'ATR_FRAMEWORK' | 'HYBRID';

export interface IchimokuCloudRequest {
  productId: string;
  name: string;
  quoteSize: string;
  paperTradeMode?: boolean;
  // Granularity is launch-locked to MEDIUM (1h) — sent to the backend as
  // strategy_metric_period: 'MEDIUM' (REQ-022). No per-request granularity field.
  stopStyle: IchimokuStopStyle;   // REQ-017
  tenkanPeriod?: number;          // Default 9
  kijunPeriod?: number;           // Default 26
  senkouBPeriod?: number;         // Default 52
  chikouDisplacement?: number;    // Default 26
  tkCrossLookback?: number;       // Default 5
  positionSizePct?: string;       // BigDecimal-as-string for precision
  maxHoldingHours?: number;       // Default 168
  // ICS-006 ATR risk framing (REQ-001). Multipliers BigDecimal-as-string.
  exitMode?: IchimokuExitMode;                    // Default ICHIMOKU_SIGNALS (DB default)
  atrStopMultiplier?: string;                     // Required when ATR_FRAMEWORK or HYBRID
  atrTargetMultiplier?: string;                   // Required when ATR_FRAMEWORK
  atrProfitLockMultiplier?: string;               // Required when ATR_FRAMEWORK or HYBRID
  atrProfitLockActivationMultiplier?: string;     // Required when ATR_FRAMEWORK or HYBRID
  atrPeriod?: number;                             // Default 14
}

/**
 * Rolling-pivot timeframes the Pivot Point strategy can read from
 * metrics.pivot_points / pivot_interactions. Mirrors backend
 * `core.model.metrics.PivotTimeframe`. Distinct from StrategyMetricPeriod.
 */
export type PivotTimeframe =
  | 'THIRTY_MIN'
  | 'ONE_HOUR'
  | 'TWO_HOUR'
  | 'FOUR_HOUR'
  | 'EIGHT_HOUR'
  | 'TWELVE_HOUR'
  | 'TWENTY_FOUR_HOUR'
  | 'SEVEN_DAY'
  | 'THIRTY_DAY';

/** Support levels the strategy may trade entries on. Subset of {S1,S2,S3}. */
export type PivotSupportLevel = 'S1' | 'S2' | 'S3';

/**
 * Tier exit percentages at the three pivot resistance levels.
 *
 * UI-LAYER convention: these are 0-100 percentages (e.g. "50", "30", "20")
 * and MUST sum to 100. The api client converts each to a decimal fraction
 * (÷100) before POST — the wire/backend use fractions that sum to 1.0.
 */
export interface PivotTierExitPcts {
  pivot: string; // % of position to exit at the pivot (PP) resistance
  r1: string;    // % to exit at R1
  r2: string;    // % to exit at R2
}

/**
 * Request type for Pivot Point Trading trade group creation (TASK-038, REQ-023).
 *
 * The UI camelCase shape; `createPivotPointTradingTradeGroup` maps each field to
 * its snake_case wire counterpart before POST (the single conversion point).
 * Percentage fields (stopLossPct, tierExitPcts.*) are 0-100 in the UI and are
 * converted to decimal fractions in the api client. Sibling: IchimokuCloudRequest.
 */
export interface PivotPointTradingRequest {
  productId: string;
  name: string;
  quoteSize: string;                          // Initial allocation in quote currency (e.g., USDC)
  paperTradeMode?: boolean;                   // Defaults to true if not specified
  strategyMetricPeriod?: StrategyMetricPeriod; // RSI/ATR candle granularity (defaults to MEDIUM)
  pivotTimeframe?: PivotTimeframe;            // Rolling-pivot series (defaults to TWENTY_FOUR_HOUR)
  enabledSupportLevels: PivotSupportLevel[];  // Non-empty subset of {S1,S2,S3}
  stopLossPct?: string;                       // % below entry support, 0-100 (UI); converted to fraction
  tieredExitEnabled?: boolean;                // Defaults to true
  tierExitPcts?: PivotTierExitPcts;           // 0-100 (UI), must sum to 100; converted to fractions
}

/** Moving-average kind the MA Crossover strategy may trade. Mirrors backend `ma_type`. */
export type MaCrossoverMaType = 'SMA' | 'EMA';

/**
 * Request type for Moving Average Crossover trade group creation (REQ-011, REQ-013).
 *
 * The UI camelCase shape; `createMaCrossoverTradeGroup` maps each field to its
 * snake_case wire counterpart before POST per `wire-contracts.md` Boundary 1.
 * The create response is consumed FLAT (no {data, metadata} envelope — AD-004).
 * Sibling: IchimokuCloudRequest.
 *
 * BigDecimal-ish fields (quoteSize, profitTargetPct, initialTrailingStopPct,
 * positionSizePct) travel as STRINGS on the wire (ICS-006 — form numbers are
 * strings). Optional fields are omitted when not provided so the backend falls
 * back to its `ma.crossover.*` config-provider defaults.
 */
export interface MaCrossoverRequest {
  productId: string;
  name: string;
  quoteSize: string;                          // Initial allocation in quote currency (e.g., USDC)
  paperTradeMode?: boolean;                   // Defaults to true if not specified
  strategyMetricPeriod?: StrategyMetricPeriod; // Candle granularity the SMA/EMA are read at (defaults to MEDIUM)
  maType?: MaCrossoverMaType;                 // SMA | EMA; backend defaults to EMA
  fastPeriod?: number;                        // Default 50; must be < slowPeriod
  slowPeriod?: number;                        // Default 200
  profitTargetPct?: string;                   // Optional per-group override (BigDecimal-as-string)
  initialTrailingStopPct?: string;            // Optional per-group override (BigDecimal-as-string)
  positionSizePct?: string;                   // Optional per-group override (BigDecimal-as-string)
}

/**
 * Request type for Support-Retest Dip Ladder trade group creation (TASK-036, REQ-021).
 *
 * SRDL reads raw candles (not calculated metrics gated by GranularityMetricConfig), so
 * the backend's StrategyMetricPeriodValidity pins it to exactly ONE valid period —
 * MEDIUM_SHORT (30-minute candles). There are no other create-time levers (no
 * ladder-geometry overrides, no fast/slow-period-equivalent knobs); every other lever
 * lives in config.global_config. The UI camelCase shape; `createSupportRetestDipLadderTradeGroup`
 * maps each field to its snake_case wire counterpart before POST. The create response is
 * consumed FLAT (no {data, metadata} envelope). `quoteSize` travels as a STRING on the
 * wire (form numbers are strings — ICS-006). Sibling: MaCrossoverRequest.
 */
export interface SupportRetestDipLadderRequest {
  productId: string;
  name: string;
  quoteSize: string;                          // Initial allocation in quote currency (e.g., USDC)
  paperTradeMode?: boolean;                   // Defaults to true if not specified
  // REQUIRED, not optional: the backend's validateInput throws 400 "strategy_metric_period
  // is required" when this is absent — there is no server-side default. Fixed to MEDIUM_SHORT
  // (30min candles), the only value StrategyMetricPeriodValidity accepts for this strategy.
  strategyMetricPeriod: StrategyMetricPeriod;
  // Optional group-tier overrides for the three SLD-001-validated tunables
  // (support_retest_tunable_config_tiers REQ-006). 0-100 percent strings, blank = inherit
  // product/global tier (never sent as "" or null on the wire -- omitted entirely).
  targetPct?: string;
  breakThresholdFraction?: string;
  floorBufferFraction?: string;
  // Optional group-tier overrides for the breakeven trail
  // (support_retest_breakeven_trailing_stop REQ-007). trailingStopEnabled is a tri-state on the
  // wire: undefined = inherit, true/false = a deliberate decision for this group. The distance is a
  // 0-100 percent string like the three above; the basis is a TrailActivationBasis name.
  trailingStopEnabled?: boolean;
  trailingStopActivationBasis?: 'POOLED_BREAKEVEN' | 'HIGHEST_LEG_BREAKEVEN';
  trailingStopDistanceFraction?: string;
}

/**
 * Request type for Trade Group Orchestrator (ORCHESTRATED_LADDER) creation
 * (TASK-032, REQ-001, REQ-032).
 *
 * The orchestrator is a standalone regime-gated ladder deployer (AD-001, AD-008):
 * it carries NO `strategyMetricPeriod` — the market-context read granularity and
 * every other operational lever live in `TradeGroupOrchestratorConfigProvider`
 * (config.global_config), not on the create request. The create form therefore
 * collects only the per-instance levers the operator sets at create time:
 * product, capital allocation, name, and the paper-trade toggle.
 *
 * The UI camelCase shape; `createOrchestratedLadderTradeGroup` maps each field to
 * its snake_case wire counterpart before POST. The create response is consumed
 * FLAT (no {data, metadata} envelope) — the backend creator returns
 * `{ trade_group_id }` verbatim. `quoteSize` travels as a STRING on the wire
 * (form numbers are strings — ICS-006). Sibling: MaCrossoverRequest.
 */
export interface OrchestratedLadderRequest {
  productId: string;
  name: string;
  quoteSize: string;         // Capital allocation in quote currency (e.g., USDC)
  paperTradeMode?: boolean;  // Defaults to true if not specified (gradual-rollout posture)
}

/**
 * Ichimoku entry/exit conditions response — real-time technical indicator
 * values + the five entry booleans + the three exit booleans.
 * Wire shape per `wire-contracts.md` Boundary 4 — snake_case keys, NO envelope.
 */
export interface IchimokuEntryConditionsResponse {
  product_id: string;
  granularity_mins: number;
  current_price: number | null;
  tenkan_sen: number | null;
  kijun_sen: number | null;
  senkou_span_a: number | null;
  senkou_span_b: number | null;
  chikou_span: number | null;
  cloud_top: number | null;
  cloud_bottom: number | null;
  tk_cross_recent: boolean;
  stop_style: string;
  current_stop_price: number | null;
  timestamp: string;
  entry_conditions: {
    price_above_cloud: boolean;
    tk_aligned: boolean;
    chikou_above_price_lag26: boolean;
    cloud_bullish: boolean;
    tk_cross_above_cloud: boolean;
    conditions_met_count: number;
  };
  exit_conditions: {
    tk_reversal: boolean;
    close_below_cloud: boolean;
    chikou_below_price_lag26: boolean;
    any_exit_fired: boolean;
  };
}

/**
 * Ichimoku cycle detail block, camelCase keys mirroring the Map returned by
 * `TradeGroupApiHandler.getIchimokuCloudDetails` (Boundary 5).
 * NOTE: the existing dispatch projects into a Map<String,Object> with
 * camelCase keys — codebase-wide inconsistency between live-conditions DTO
 * (snake_case) and cycle-detail Map (camelCase) is documented in wire-contracts.
 */
export interface IchimokuCycleDetail {
  entryTenkanSen: number | null;
  entryKijunSen: number | null;
  entrySenkouSpanA: number | null;
  entrySenkouSpanB: number | null;
  entryChikouSpan: number | null;
  entryCloudTop: number | null;
  entryCloudBottom: number | null;
  entrySignalComponents: string | null;
  stopStyle: string | null;
  stopLossBasis: number | null;
  stopLossPrice: number | null;
  exitTenkanSen: number | null;
  exitKijunSen: number | null;
  exitSenkouSpanA: number | null;
  exitSenkouSpanB: number | null;
  exitChikouSpan: number | null;
  exitCloudTop: number | null;
  exitCloudBottom: number | null;
}

/**
 * Request type for canceling a trade group
 */
export interface CancelTradeGroupRequest {
  tradeGroupId: string;
  productId: string;
  minPrice?: string | null;
  maxPrice?: string | null;
  tradeSide?: 'BUY' | 'SELL' | null;
  preview: boolean;
}

/**
 * Preview of a trade that will be cancelled
 */
export interface CancelledTradePreview {
  orderId: string;
  clientOrderId: string;
  side: string;
  limitPrice: string;
  baseSize: string;
  status: string;
}

/**
 * Response from canceling a trade group
 */
export interface CancelTradeGroupResponse {
  buyOrdersCancelled: number;
  sellOrdersCancelled: number;
  tradesPreviewed: CancelledTradePreview[] | null;
  message: string;
}

